import { useEffect, useRef, useState } from 'react'
import NearbyList from '@components/NearbyList'
import MapView from '@components/MapView'
import LanguageSelector from '@components/LanguageSelector'
import Bookmarks from '@components/Bookmarks'
import CityPackDownload from '@components/CityPackDownload'
import { getSetting, setSetting } from '@services/indexeddb'
import DevLocation from '@components/DevLocation'

export default function App() {
  const [lang, setLang] = useState<string>('en')
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])
  const [voice, setVoice] = useState<string>('')
  const [rate, setRate] = useState<number>(1)
  const [pitch, setPitch] = useState<number>(1)
  const [activeTab, setActiveTab] = useState<'map' | 'poi' | 'saved'>('map')
  const [focusCity, setFocusCity] = useState('Detecting location...')
  const voicePreviewReady = useRef(false)

  useEffect(() => {
    getSetting('lang').then(v => { if (v) setLang(v as string) })
  }, [])
  useEffect(() => { setSetting('lang', lang) }, [lang])
  useEffect(() => {
    const preloadCity = (import.meta as any).env?.VITE_PRELOAD_CITY as string | undefined
    if (!preloadCity) return
    let active = true
    ;(async () => {
      const { getSetting, setSetting, savePOIContent, putPOIs, getPOICount } = await import('@services/indexeddb')
      const done = await getSetting(`download:${preloadCity}`)
      const count = await getPOICount()
      if (done && count > 0) return
      try {
        const slug = preloadCity.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
        const langCode = (lang || 'en').split('-')[0] || 'en'
        const res = await fetch(`/packs/${slug}-${langCode}.json`)
        const resEn = !res.ok && langCode !== 'en' ? await fetch(`/packs/${slug}-en.json`) : null
        const chosen = res.ok ? res : (resEn && resEn.ok ? resEn : null)
        if (chosen) {
          const pack = await chosen.json()
          if (!active) return
          await putPOIs(pack.items)
          for (const it of pack.items) {
            if (!active) break
            const d = pack[it.id]
            if (d) await savePOIContent(it.id, pack.lang || 'en', d)
          }
          await setSetting(`download:${preloadCity}`, { city: preloadCity, lang, ts: Date.now() })
          return
        }
        const { buildCityPack, fetchPOI } = await import('@services/api')
        const token = await getSetting('admin_token')
        const manifest = await buildCityPack(preloadCity, lang, token || undefined)
        if (!active) return
        await putPOIs(manifest.items)
        for (const it of manifest.items) {
          if (!active) break
          if (it.id) {
            const d = await fetchPOI(it.id, lang)
            await savePOIContent(it.id, lang, d)
          }
        }
        await setSetting(`download:${preloadCity}`, { city: preloadCity, lang, ts: Date.now() })
      } catch (e) {
        // silent fail: manual download remains available
      }
    })()
    return () => { active = false }
  }, [lang])
  useEffect(() => {
    const synth = window.speechSynthesis
    if (!synth) return
    const update = () => {
      const all = synth.getVoices()
      const filtered = all.filter(v => v.lang?.startsWith(lang) || v.lang?.startsWith('en'))
      setVoices(filtered)
    }
    update()
    synth.addEventListener('voiceschanged', update)
    getSetting('voice').then(v => { if (v) setVoice(String(v)) })
    getSetting('voice_rate').then(v => { if (v !== undefined && v !== null) setRate(Number(v)) })
    getSetting('voice_pitch').then(v => { if (v !== undefined && v !== null) setPitch(Number(v)) })
    return () => synth.removeEventListener('voiceschanged', update)
  }, [lang])
  useEffect(() => { if (voice) setSetting('voice', voice) }, [voice])
  useEffect(() => { setSetting('voice_rate', rate) }, [rate])
  useEffect(() => { setSetting('voice_pitch', pitch) }, [pitch])
  useEffect(() => {
    const onFocus = (e: Event) => {
      const ce = e as CustomEvent<{ city?: string; country?: string }>
      if (ce.detail?.city && ce.detail?.country) setFocusCity(`${ce.detail.city}, ${ce.detail.country}`)
      else if (ce.detail?.city) setFocusCity(ce.detail.city)
    }
    window.addEventListener('focus-changed', onFocus as EventListener)
    return () => window.removeEventListener('focus-changed', onFocus as EventListener)
  }, [])
  useEffect(() => {
    if (!voicePreviewReady.current) {
      voicePreviewReady.current = true
      return
    }
    const synth = window.speechSynthesis
    if (!synth) return
    synth.cancel()
    const utter = new SpeechSynthesisUtterance('Voice preview')
    const all = synth.getVoices()
    const v = all.find(x => x.name === voice) || all.find(x => x.lang?.startsWith(lang)) || all.find(x => x.lang?.startsWith('en'))
    if (v) {
      utter.voice = v
      utter.lang = v.lang
    }
    utter.rate = rate
    utter.pitch = pitch
    synth.speak(utter)
  }, [voice, rate, pitch, lang])

  return (
    <>
      <header className="bg-surface dark:bg-[#191c1d] font-headline tracking-tight antialiased flex flex-wrap justify-between items-center w-full md:px-8 px-4 py-4 h-20 fixed z-[2000] top-0 left-0 border-b border-outline-variant/20">
        <div className="flex items-center gap-4 md:gap-8">
          <span className="text-xl md:text-2xl font-extrabold text-primary dark:text-teal-500">Digital Concierge</span>
          <nav className="hidden md:flex items-center gap-6">
            <button onClick={() => setActiveTab('map')} className={`pb-1 font-bold ${activeTab === 'map' ? 'text-primary border-b-2 border-tertiary' : 'text-on-surface-variant hover:text-primary transition-colors'}`}>Explore</button>
            <button onClick={() => setActiveTab('poi')} className={`pb-1 font-bold ${activeTab === 'poi' ? 'text-primary border-b-2 border-tertiary' : 'text-on-surface-variant hover:text-primary transition-colors'}`}>POIs</button>
            <button onClick={() => setActiveTab('saved')} className={`pb-1 font-bold ${activeTab === 'saved' ? 'text-primary border-b-2 border-tertiary' : 'text-on-surface-variant hover:text-primary transition-colors'}`}>Saved</button>
          </nav>
        </div>
        <div className="flex items-center gap-2 md:gap-4">
          <LanguageSelector value={lang} onChange={setLang} />
          <CityPackDownload lang={lang} />
          <button className="p-2 hover:bg-surface-container-low dark:hover:bg-slate-800 rounded-lg transition-all text-primary hidden md:block">
            <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>account_circle</span>
          </button>
        </div>
      </header>
      
      <aside className="hidden md:flex fixed left-0 top-20 h-[calc(100vh-5rem)] w-72 bg-surface dark:bg-[#191c1d] flex-col p-6 space-y-4 font-body text-sm font-medium z-40 border-r border-outline-variant/20">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-outline mb-1">Current Focus</p>
          <h2 className="text-lg font-bold text-on-surface">{focusCity}</h2>
        </div>
        
        <div className="space-y-1">
          <button onClick={() => setActiveTab('map')} className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${activeTab === 'map' ? 'bg-primary text-white shadow-lg shadow-primary/10 translate-x-1 duration-300' : 'text-on-surface-variant hover:bg-outline-variant/20'}`}>
            <span className="material-symbols-outlined">map</span>
            <span>Map View</span>
          </button>
          <button onClick={() => setActiveTab('poi')} className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${activeTab === 'poi' ? 'bg-primary text-white shadow-lg shadow-primary/10 translate-x-1 duration-300' : 'text-on-surface-variant hover:bg-outline-variant/20'}`}>
            <span className="material-symbols-outlined">list_alt</span>
            <span>Points of Interest</span>
          </button>
          <button onClick={() => setActiveTab('saved')} className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${activeTab === 'saved' ? 'bg-primary text-white shadow-lg shadow-primary/10 translate-x-1 duration-300' : 'text-on-surface-variant hover:bg-outline-variant/20'}`}>
            <span className="material-symbols-outlined">bookmark</span>
            <span>Saved Places</span>
          </button>
        </div>

        <div className="mt-auto pt-6 border-t border-outline-variant/20 space-y-4">
          <div>
             <div className="flex items-center gap-2 mb-2 text-on-surface-variant">
               <span className="material-symbols-outlined">record_voice_over</span>
               <span className="font-bold text-xs uppercase tracking-wider">Voice</span>
             </div>
            <select className="w-full text-sm p-2 rounded-lg bg-surface-container-low border-none mb-4 outline-none text-on-surface" value={voice} onChange={e => setVoice(e.target.value)}>
              <option value="">Default voice</option>
              {voices.map(v => (
                <option key={v.name} value={v.name}>{v.name}</option>
              ))}
            </select>
            <div className="flex flex-col gap-2 text-xs text-on-surface-variant">
              <label className="flex justify-between items-center">Pitch <span>{pitch.toFixed(1)}</span></label>
              <input type="range" className="w-full h-1 bg-outline-variant rounded-lg appearance-none cursor-pointer" min={0.5} max={2} step={0.1} value={pitch} onChange={e => setPitch(Number(e.target.value))} />
              <label className="flex justify-between items-center mt-2">Rate <span>{rate.toFixed(2)}</span></label>
              <input type="range" className="w-full h-1 bg-outline-variant rounded-lg appearance-none cursor-pointer" min={0.75} max={1.5} step={0.05} value={rate} onChange={e => setRate(Number(e.target.value))} />
            </div>
          </div>
        </div>
      </aside>
      
      <main className="md:ml-72 mt-20 h-[calc(100vh-9rem)] md:h-[calc(100vh-5rem)] flex overflow-hidden">
        {/* Left Pane: POI List or Saved Items */}
        <section className={`w-full md:w-2/5 h-full bg-surface-container-low overflow-hidden flex flex-col ${activeTab === 'map' ? 'hidden md:flex' : ''}`}>
          {activeTab === 'saved' ? (
            <div className="p-4 md:p-8 h-full overflow-y-auto no-scrollbar">
              <header className="mb-6">
                <h1 className="font-headline text-3xl font-extrabold text-on-surface tracking-tight mb-2">Saved Places</h1>
              </header>
              <div className="bg-surface-container-lowest rounded-xl p-4 shadow-sm">
                 <Bookmarks lang={lang} />
              </div>
            </div>
          ) : (
            <NearbyList lang={lang} />
          )}
        </section>
        
        {/* Right Pane: Interactive Map */}
        <section className={`w-full md:w-3/5 h-full relative ${activeTab === 'map' ? 'block' : 'hidden md:block'}`}>
          <div className="absolute inset-0 z-0">
             <MapView lang={lang} />
          </div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[90%] max-w-2xl z-[1000] pointer-events-none">
             <div className="pointer-events-auto">
                <DevLocation />
             </div>
          </div>
        </section>
      </main>
      
      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-surface border-t border-outline-variant/20 flex justify-around items-center z-[2000] pb-env-safe shadow-lg">
        <button onClick={() => setActiveTab('map')} className={`flex flex-col items-center gap-1 w-full h-full justify-center transition-colors ${activeTab === 'map' ? 'text-primary' : 'text-on-surface-variant hover:bg-surface-container-low'}`}>
          <span className="material-symbols-outlined" style={{ fontVariationSettings: activeTab === 'map' ? "'FILL' 1" : "'FILL' 0" }}>map</span>
          <span className="text-[10px] font-bold">Map</span>
        </button>
        <button onClick={() => setActiveTab('poi')} className={`flex flex-col items-center gap-1 w-full h-full justify-center transition-colors ${activeTab === 'poi' ? 'text-primary' : 'text-on-surface-variant hover:bg-surface-container-low'}`}>
          <span className="material-symbols-outlined" style={{ fontVariationSettings: activeTab === 'poi' ? "'FILL' 1" : "'FILL' 0" }}>list_alt</span>
          <span className="text-[10px] font-bold">Places</span>
        </button>
        <button onClick={() => setActiveTab('saved')} className={`flex flex-col items-center gap-1 w-full h-full justify-center transition-colors ${activeTab === 'saved' ? 'text-primary' : 'text-on-surface-variant hover:bg-surface-container-low'}`}>
          <span className="material-symbols-outlined" style={{ fontVariationSettings: activeTab === 'saved' ? "'FILL' 1" : "'FILL' 0" }}>bookmark</span>
          <span className="text-[10px] font-bold">Saved</span>
        </button>
      </nav>

      <footer className="hidden md:block text-[11px] text-on-surface-variant px-4 py-2 md:px-8 border-t border-outline-variant/20 bg-surface">
        Content and images from Wikipedia/Wikimedia where indicated · CC BY‑SA
      </footer>
    </>
  )
}
